import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Placement } from "@floating-ui/react";
import { FloatingTooltip } from "../Tooltip/FloatingTooltip";
import usePrefersReducedMotion from "../../util/usePrefersReducedMotion";
import {
  EDGE_FADE_PX,
  RETURN_MS,
  SCROLL_DELAY_MS,
  maskKeyframes,
  overflowDistancePx,
  sweepDurationMs,
} from "./truncatedTextMath";

type TruncatedTextTag = "span" | "p" | "div" | "h2" | "h3" | "h4";

type TruncatedTextProps = {
  text: string;
  as?: TruncatedTextTag;
  className?: string;
  placement?: Placement;
};

const NO_HOVER_QUERY = "(hover: none), (pointer: coarse)";
const TRIGGER_SELECTOR = "[data-truncate-trigger]";
const INTERACTIVE_SELECTOR = [
  "a",
  "button",
  "[role=button]",
  "label",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

const useNoHover = (): boolean => {
  const [value, setValue] = useState(
    () => typeof window !== "undefined" && window.matchMedia(NO_HOVER_QUERY).matches,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const mql = window.matchMedia(NO_HOVER_QUERY);
    const onChange = () => setValue(mql.matches);
    mql.addEventListener("change", onChange);
    return () => {
      mql.removeEventListener("change", onChange);
    };
  }, []);

  return value;
};

const hasVisibleFocusWithin = (element: Element): boolean => {
  try {
    return element.matches(":focus-visible") || element.querySelector(":focus-visible") !== null;
  } catch {
    return element.contains(document.activeElement);
  }
};

const currentOffsetPx = (element: HTMLElement): number => {
  const transform = getComputedStyle(element).transform;
  if (!transform || transform === "none" || typeof DOMMatrixReadOnly === "undefined") {
    return 0;
  }
  return new DOMMatrixReadOnly(transform).m41;
};

type Motion = { text: Animation; mask: Animation | null };

const cancelMotion = (motion: Motion | null) => {
  motion?.text.cancel();
  motion?.mask?.cancel();
};

const move = (
  span: HTMLElement,
  root: HTMLElement | null,
  fromPx: number,
  toPx: number,
  overflowPx: number,
  duration: number,
  fill: FillMode,
): Motion => {
  const text = span.animate(
    [{ transform: `translateX(${fromPx}px)` }, { transform: `translateX(${toPx}px)` }],
    { duration, easing: "linear", fill },
  );
  const mask = root
    ? root.animate(maskKeyframes(fromPx, toPx, overflowPx), {
        duration,
        easing: "linear",
        fill,
      })
    : null;
  return { text, mask };
};

const sweep = (
  span: HTMLElement,
  root: HTMLElement | null,
  overflowPx: number,
  previous: Motion | null,
): Motion => {
  const from = previous ? currentOffsetPx(span) : 0;
  cancelMotion(previous);
  const to = -(overflowPx + EDGE_FADE_PX);
  return move(span, root, from, to, overflowPx, sweepDurationMs(from - to), "forwards");
};

export const TruncatedText = ({
  text,
  as: Tag = "span",
  className = "",
  placement,
}: TruncatedTextProps) => {
  const rootRef = useRef<HTMLElement | null>(null);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationRef = useRef<Motion | null>(null);
  const overflowRef = useRef(0);
  const pendingDistanceRef = useRef<number | null>(null);
  const hoveredRef = useRef(false);
  const focusedRef = useRef(false);
  const [active, setActive] = useState(false);
  const activeRef = useRef(false);
  activeRef.current = active;
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const prefersReducedMotion = usePrefersReducedMotion();
  const noHover = useNoHover();
  const useTooltip = prefersReducedMotion || noHover;
  const useTooltipRef = useRef(useTooltip);
  useTooltipRef.current = useTooltip;

  const measure = () => overflowDistancePx(rootRef.current, textRef.current);

  useLayoutEffect(() => {
    const span = textRef.current;
    const distance = pendingDistanceRef.current;
    if (!active || !span || distance === null) {
      return;
    }
    pendingDistanceRef.current = null;
    animationRef.current = sweep(span, rootRef.current, distance, animationRef.current);
  }, [active]);

  const setRoot = useCallback((node: HTMLElement | null) => {
    rootRef.current = node;
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return undefined;
    }
    const trigger: HTMLElement =
      root.closest<HTMLElement>(TRIGGER_SELECTOR) ??
      root.closest<HTMLElement>(INTERACTIVE_SELECTOR) ??
      root;

    const clearDelay = () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
    };

    const startScroll = () => {
      const distance = measure();
      const span = textRef.current;
      if (distance === 0 || !span) {
        return;
      }
      overflowRef.current = distance;
      if (activeRef.current) {
        animationRef.current = sweep(span, root, distance, animationRef.current);
        return;
      }
      pendingDistanceRef.current = distance;
      setActive(true);
    };

    const engage = () => {
      clearDelay();
      delayTimerRef.current = setTimeout(() => {
        delayTimerRef.current = null;
        if (useTooltipRef.current) {
          setTooltipOpen(measure() > 0);
        } else {
          startScroll();
        }
      }, SCROLL_DELAY_MS);
    };

    const release = () => {
      clearDelay();
      setTooltipOpen(false);
      pendingDistanceRef.current = null;
      const span = textRef.current;
      const animation = animationRef.current;
      if (!span || !animation) {
        setActive(false);
        return;
      }
      const from = currentOffsetPx(span);
      cancelMotion(animation);
      const back = move(span, root, from, 0, overflowRef.current, RETURN_MS, "none");
      animationRef.current = back;
      back.text.finished
        .then(() => {
          if (animationRef.current === back) {
            animationRef.current = null;
            setActive(false);
          }
        })
        .catch(() => undefined);
    };

    const isEngaged = () => hoveredRef.current || focusedRef.current;

    const onPointerEnter = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" && event.pointerType !== "pen") {
        return;
      }
      const wasEngaged = isEngaged();
      hoveredRef.current = true;
      if (!wasEngaged) {
        engage();
      }
    };

    const onPointerLeave = () => {
      hoveredRef.current = false;
      if (!isEngaged()) {
        release();
      }
    };

    const onFocusIn = () => {
      if (focusedRef.current || !hasVisibleFocusWithin(trigger)) {
        return;
      }
      const wasEngaged = isEngaged();
      focusedRef.current = true;
      if (!wasEngaged) {
        engage();
      }
    };

    const onFocusOut = (event: FocusEvent) => {
      if (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget)) {
        return;
      }
      focusedRef.current = false;
      if (!isEngaged()) {
        release();
      }
    };

    trigger.addEventListener("pointerenter", onPointerEnter);
    trigger.addEventListener("pointerleave", onPointerLeave);
    trigger.addEventListener("focusin", onFocusIn);
    trigger.addEventListener("focusout", onFocusOut);
    return () => {
      trigger.removeEventListener("pointerenter", onPointerEnter);
      trigger.removeEventListener("pointerleave", onPointerLeave);
      trigger.removeEventListener("focusin", onFocusIn);
      trigger.removeEventListener("focusout", onFocusOut);
      clearDelay();
      cancelMotion(animationRef.current);
      animationRef.current = null;
    };
  }, []);

  return (
    <FloatingTooltip
      content={text}
      placement={placement}
      disabled={!useTooltip}
      open={tooltipOpen}
      onOpenChange={setTooltipOpen}
      shouldOpen={() => measure() > 0}
      testId="truncated-text-tooltip"
      ariaHidden
    >
      <Tag
        ref={setRoot}
        className={`block min-w-0 overflow-hidden whitespace-nowrap ${
          active ? "" : "text-ellipsis"
        } ${className}`}
      >
        <span ref={textRef} className={active ? "inline-block" : ""}>
          {text}
        </span>
      </Tag>
    </FloatingTooltip>
  );
};
